import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type {
  DataTableColumn,
  DataTableRenderCellParams,
  DataTableRowEventHandler,
  DataTableRowModel
} from '@/components/custom/erp/data-table-v2';
import { ContextMenu, ContextMenuBuilder } from '@/components/custom/erp/input-table-v2';
import type { ButtonType } from '@/components/custom/erp/input-table-v2';
import type { RepeatRowsPayload } from '@/components/custom/erp';
import { ErpInputTableV2 } from '@/components/custom/erp';
import { copyCellValue } from '@/lib/table-handlers';
import { FormMode } from '@/types/form';

interface EditableTableWithContextProps<T extends DataTableRowModel> {
  formMode: FormMode;
  rows: T[];
  columns: DataTableColumn[];
  selectedRowId?: string;
  selectedCell?: { id: string; field: string } | null;
  onRowClick?: DataTableRowEventHandler<T>;
  onCellClick?: (params: DataTableRenderCellParams<T>) => void;
  actions: Partial<Record<ButtonType, (clipboardText?: string) => void | Promise<void>>>;
  actionOrder: ButtonType[];
  onRepeatForRows?: (payload: RepeatRowsPayload<T>) => void;
  autoHeight?: boolean;
  bottomSelector?: string;
}

export function EditableTableWithContext<T extends DataTableRowModel>({
  formMode,
  rows,
  columns,
  selectedRowId,
  selectedCell,
  onRowClick,
  onCellClick,
  actions,
  actionOrder,
  onRepeatForRows,
  autoHeight = true,
  bottomSelector = '[data-arito-bottom-bar]'
}: EditableTableWithContextProps<T>) {
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: T | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: row => row?.uuid,
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({ columnField, columnValue, columnValues });
        },
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, onRepeatForRows, rows.length]
  );

  const handleCellContextMenu = useCallback(
    (event: MouseEvent | ReactMouseEvent, params: DataTableRenderCellParams<T>) => {
      onCellClick?.(params);
      setContextMenu({
        position: { x: event.clientX, y: event.clientY },
        rowData: params.row,
        columnField: params.field
      });
    },
    [onCellClick]
  );

  return (
    <>
      <ErpInputTableV2
        rows={rows}
        columns={columns}
        getRowId={row => row?.uuid || ''}
        selectedRowId={selectedRowId}
        pasteTarget={selectedCell}
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        onCellContextMenu={handleCellContextMenu}
        actionsDisabled={formMode === 'view'}
        actions={actions}
        actionOrder={actionOrder}
        autoHeight={autoHeight}
        bottomSelector={bottomSelector}
      />
      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() =>
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          })
        }
      />
    </>
  );
}
